// Kariyer Online - Motivasyon Sesleri Hook
import { useCallback, useRef, useEffect } from 'react';

// Web Audio API ile basit ses oluşturucu - CORS sorunsuz
const createToneSound = (frequency, duration, type = 'sine', volume = 0.3) => {
  return () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + duration);
    } catch (e) {
      console.log('Audio not available');
    }
  };
};

// Çoklu ton ile melodi oluştur
const playMelody = (notes, volume = 0.3) => {
  return () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      notes.forEach(({ freq, start, duration, type = 'sine' }) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.type = type;
        oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + start);
        
        gainNode.gain.setValueAtTime(volume, audioContext.currentTime + start);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + start + duration);
        
        oscillator.start(audioContext.currentTime + start);
        oscillator.stop(audioContext.currentTime + start + duration);
      });
    } catch (e) {
      console.log('Audio not available');
    }
  };
};

// Ses efektleri - Web Audio API ile oluşturulmuş
const SOUNDS = {
  // Doğru cevap - Mutlu "ding!" sesi (C5 - E5)
  correct: playMelody([
    { freq: 523.25, start: 0, duration: 0.15, type: 'sine' },
    { freq: 659.25, start: 0.1, duration: 0.2, type: 'sine' }
  ], 0.4),
  
  // Yanlış cevap - Düşük "bonk" sesi
  incorrect: playMelody([
    { freq: 200, start: 0, duration: 0.15, type: 'triangle' },
    { freq: 150, start: 0.1, duration: 0.2, type: 'triangle' }
  ], 0.3),
  
  // Ders tamamlama - Zafer fanfarı
  lessonComplete: playMelody([
    { freq: 523.25, start: 0, duration: 0.15, type: 'sine' },
    { freq: 659.25, start: 0.15, duration: 0.15, type: 'sine' },
    { freq: 783.99, start: 0.3, duration: 0.15, type: 'sine' },
    { freq: 1046.50, start: 0.45, duration: 0.3, type: 'sine' }
  ], 0.35),
  
  // Seviye tamamlama - Büyük zafer melodisi
  levelComplete: playMelody([
    { freq: 392, start: 0, duration: 0.12, type: 'square' },
    { freq: 523.25, start: 0.12, duration: 0.12, type: 'square' },
    { freq: 659.25, start: 0.24, duration: 0.12, type: 'square' },
    { freq: 783.99, start: 0.36, duration: 0.12, type: 'square' },
    { freq: 1046.50, start: 0.48, duration: 0.4, type: 'sine' }
  ], 0.3),
  
  // Combo sesi - Hızlı yükselen tonlar
  combo: playMelody([
    { freq: 440, start: 0, duration: 0.08, type: 'sawtooth' },
    { freq: 554.37, start: 0.08, duration: 0.08, type: 'sawtooth' },
    { freq: 659.25, start: 0.16, duration: 0.08, type: 'sawtooth' },
    { freq: 880, start: 0.24, duration: 0.15, type: 'sine' }
  ], 0.25),
  
  // Mil kazanma - Coin sesi
  xpGain: playMelody([
    { freq: 1318.51, start: 0, duration: 0.08, type: 'sine' },
    { freq: 1567.98, start: 0.08, duration: 0.12, type: 'sine' }
  ], 0.3),
  
  // Tıklama sesi
  click: createToneSound(800, 0.05, 'sine', 0.15),
  
  // Kart çevirme
  cardFlip: playMelody([
    { freq: 600, start: 0, duration: 0.05, type: 'sine' },
    { freq: 900, start: 0.05, duration: 0.08, type: 'sine' }
  ], 0.2),
  
  // Hoşgeldin sesi
  welcome: playMelody([
    { freq: 440, start: 0, duration: 0.15, type: 'sine' },
    { freq: 554.37, start: 0.15, duration: 0.15, type: 'sine' },
    { freq: 659.25, start: 0.3, duration: 0.2, type: 'sine' }
  ], 0.25),
  
  // Badge kazanma
  badge: playMelody([
    { freq: 783.99, start: 0, duration: 0.1, type: 'sine' },
    { freq: 987.77, start: 0.1, duration: 0.1, type: 'sine' },
    { freq: 1174.66, start: 0.2, duration: 0.1, type: 'sine' },
    { freq: 1567.98, start: 0.3, duration: 0.3, type: 'sine' }
  ], 0.3),
  
  // Perfect sesi - %100 doğruluk
  perfect: playMelody([
    { freq: 523.25, start: 0, duration: 0.1, type: 'sine' },
    { freq: 659.25, start: 0.1, duration: 0.1, type: 'sine' },
    { freq: 783.99, start: 0.2, duration: 0.1, type: 'sine' },
    { freq: 1046.50, start: 0.3, duration: 0.15, type: 'sine' },
    { freq: 1318.51, start: 0.45, duration: 0.15, type: 'sine' },
    { freq: 1567.98, start: 0.6, duration: 0.3, type: 'sine' }
  ], 0.35),
};

export const useSounds = () => {
  const soundEnabled = useRef(true);
  const volumeRef = useRef(0.5);
  
  // LocalStorage'dan ayarları al
  useEffect(() => {
    const savedSoundEnabled = localStorage.getItem('kariyerSoundEnabled');
    const savedVolume = localStorage.getItem('kariyerSoundVolume');
    
    if (savedSoundEnabled !== null) {
      soundEnabled.current = savedSoundEnabled === 'true';
    }
    if (savedVolume !== null) {
      volumeRef.current = parseFloat(savedVolume);
    }
  }, []);
  
  // Ses çal
  const playSound = useCallback((soundName) => {
    if (!soundEnabled.current) return;
    
    try {
      const soundFn = SOUNDS[soundName];
      if (soundFn) {
        soundFn();
      }
    } catch (error) {
      console.log('Ses hatası:', error);
    }
  }, []);
  
  // Doğru cevap sesi
  const playCorrect = useCallback(() => {
    playSound('correct');
  }, [playSound]);
  
  // Yanlış cevap sesi
  const playIncorrect = useCallback(() => {
    playSound('incorrect');
  }, [playSound]);
  
  // Ders tamamlama sesi
  const playLessonComplete = useCallback(() => {
    playSound('lessonComplete');
  }, [playSound]);
  
  // Seviye tamamlama sesi
  const playLevelComplete = useCallback(() => {
    playSound('levelComplete');
  }, [playSound]);
  
  // Streak sesi
  const playStreak = useCallback(() => {
    playSound('combo');
  }, [playSound]);
  
  // Mil kazanma sesi
  const playXpGain = useCallback(() => {
    playSound('xpGain');
  }, [playSound]);
  
  // Kart çevirme sesi
  const playCardFlip = useCallback(() => {
    playSound('cardFlip');
  }, [playSound]);
  
  // Badge kazanma sesi
  const playBadge = useCallback(() => {
    playSound('badge');
  }, [playSound]);
  
  // Combo sesi
  const playCombo = useCallback(() => {
    playSound('combo');
  }, [playSound]);
  
  // Perfect sesi
  const playPerfect = useCallback(() => {
    playSound('perfect');
  }, [playSound]);
  
  // Tıklama sesi
  const playClick = useCallback(() => {
    playSound('click');
  }, [playSound]);
  
  // Hoşgeldin sesi
  const playWelcome = useCallback(() => {
    playSound('welcome');
  }, [playSound]);
  
  // Ses ayarlarını değiştir
  const setSoundEnabled = useCallback((enabled) => {
    soundEnabled.current = enabled;
    localStorage.setItem('kariyerSoundEnabled', enabled.toString());
  }, []);
  
  const setVolume = useCallback((volume) => {
    volumeRef.current = Math.max(0, Math.min(1, volume));
    localStorage.setItem('kariyerSoundVolume', volumeRef.current.toString());
  }, []);
  
  const isSoundEnabled = useCallback(() => soundEnabled.current, []);
  const getVolume = useCallback(() => volumeRef.current, []);
  
  return {
    playSound,
    playCorrect,
    playIncorrect,
    playLessonComplete,
    playLevelComplete,
    playStreak,
    playXpGain,
    playCardFlip,
    playBadge,
    playCombo,
    playPerfect,
    playClick,
    playWelcome,
    setSoundEnabled,
    setVolume,
    isSoundEnabled,
    getVolume,
  };
};

export default useSounds;
